import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Collapse,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { LoadingButton } from "@material-ui/lab";
import { useSnackbar } from "notistack5";
import FavoriteRoundedIcon from "@material-ui/icons/FavoriteRounded";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import useWebotServer from "hooks/useWebotServer";
import useContactWrites from "hooks/contacts/useContactWrites";
import { useAddListingWithEditsToBuyers } from "hooks/useAddListingWithEditsToBuyers";
import {
  createFavoriteIfNotExistsAndAddListingToFavorites,
  removeListingFromFavorites
} from "utils/Haim/firestoreUtils";
import type { ListingPriorEdits } from "utils/Haim/firestoreUtils";
import { PATH_DASHBOARD, PATH_PUBLIC } from "routes/paths";
import GroupAddRoundedIcon from "@material-ui/icons/GroupAddRounded";
import CrmBulkAddToAudienceDialog from "../../crm/bulk/CrmBulkAddToAudienceDialog";
import { getFullAddress, parseListingAddressByDisplayMode } from "utils/listings/display";
import { EditListingInMinisiteDialog } from "../../listingsMatch/EditListingInMinisiteDialog";
import { NewBulkMessageDialog } from "../../contacts/modal/bulkMessage/NewBulkMessageDialog";
import { NoBuyersFoundIllustration } from "../NoBuyersFoundIllustration";
import { MatchReasonDetails } from "../MatchReasonDetails";
import ListingShareOfficialDialog from "./ListingShareOfficialDialog";
import ListingShareWhatsappChooserDialog from "./ListingShareWhatsappChooserDialog";
import type { WhatsappSendChannel } from "./ListingShareWhatsappChooserDialog";
import {
  MatchBudgetCell,
  MatchMissingBadge,
  MatchDataWarning,
  MatchDetailsToggle,
  MatchLooseBadge,
  MatchIdentity,
  MatchMinisiteCell,
  MatchRowActions,
  MatchScore
} from "./ListingMatchRowParts";
import { ContactPanelDrawer } from "../../contacts/detail/ContactPanelDrawer";
import useOrgDirectory from "hooks/useOrgDirectory";
import { useLocalStorage } from "usehooks-ts";
import { CRM_LAST_ORG_STORAGE_KEY, resolveCrmOrgId } from "../../crm/crmView";
import {
  applyMatchRowPatch,
  buildMatchRows,
  countMatchGaps,
  defaultMatchAudienceName,
  toAudienceMembers,
  partitionMatchRowsByQuality,
  isOfficeRecordRow,
  isRowSelectable,
  rowsForScope,
  toCrmPanelRow,
  uniqueRecipientRows
} from "./matchRows";
import { useContainerWidth } from "hooks/useContainerWidth";
import { useListingMinisiteState } from "./useListingMinisiteState";
import type { MatchRow, MatchRowPatch, MatchScope } from "./matchRows";
import type { CrmContactRow } from "../../../../@types/webotServer";
import type { MatchAudience } from "../matchAudience";
import type { AlgoliaHit } from "instantsearch.js";
import type {
  BulkMessageSentMessage,
  ListingListedFor,
  ProcessedListingData,
  RealEstateContact,
  REST_schema
} from "../../../../@types/sharedSchema";

type ListingLike = Partial<ProcessedListingData> & { objectID?: string };

type Props = {
  listing: ListingLike | null | undefined;
  listingId: string;
  audience: MatchAudience;
  /** The viewer's own matches. `undefined` = not known yet (renders a skeleton). */
  myContacts?: Partial<REST_schema.RealEstateContactWithConfidence>[];
  /** Colleagues' matches. `undefined` = not loaded yet — NOT "none". */
  orgGroups?: REST_schema.RealtorsWithContactsForListing[];
  /**
   * Whether this realtor may see the office's buyers AT ALL, from the agency's CRM permissions —
   * the server's answer (`RelevantContactsForListingResponse.orgAccess`), not a guess from
   * "is in an agency". `"hidden"` removes the office toggle entirely. `undefined` = not known yet
   * (an older response, or nothing loaded), which falls back to the old heuristic.
   */
  orgAccess?: REST_schema.RelevantContactsForListingResponse["orgAccess"];
  /** A live compute is in flight; the rows on screen (if any) are the previous answer. */
  refreshing?: boolean;
  /**
   * The rows are the server's last SNAPSHOT, not a live answer. Its own rows are a projection of
   * the contact, so the extension bulk send (it reads `row.contact` for skip flags and template
   * variables) waits for the live answer; its tooltip reads `refreshing` to say whether one is
   * coming. The official send, minisite and row actions need only the phone and stay on.
   */
  provisional?: boolean;
  /** The snapshot hit the server's size cap and holds only the first rows. */
  truncated?: boolean;
  /** Tighter rows for the batch-match dialog. */
  dense?: boolean;
  /** A contact got the listing in their minisite — the batch dialog tracks these for its bulk message. */
  onContactAddedToMinisite?: (phone: string) => void;
};

type BulkAction = {
  key: string;
  label: string;
  /**
   * The mobile tiles are an equal-width grid inside ~256px on a phone, so a long label wraps to
   * three lines and the row grows. Used there in place of `label` when set.
   */
  shortLabel?: string;
  icon: JSX.Element;
  onClick: () => void;
  loading?: boolean;
  /** Set = the action is disabled, and this is the tooltip saying why. */
  disabledReason?: string;
};

/** What the side panel's own-row patch can change on a match row (see applyMatchRowPatch). */
const PANEL_PATCH_KEYS = ["name", "firstName", "lastName", "crmIsHot", "crmStatus", "crmSubStatus", "budget", "budgetRent"] as const;

/**
 * The desktop checkbox cell. MUI sizes `padding="checkbox"` as a fixed 48px — a 20px control plus
 * 28px of cell padding — and gets there by stripping the control's own 9px padding with
 * `.MuiTableCell-paddingCheckbox > * { padding: 0 }`. That selector matches the DIRECT child, and
 * the row checkbox is wrapped (its tooltip needs an element that receives events), so the reset
 * has to be restated here or the cell overflows its own column. See `rowCheckbox`.
 */
const CHECKBOX_CELL_SX = { "& .MuiCheckbox-root": { padding: 0 } } as const;

const AUDIENCE_NOUN: Record<MatchAudience, { plural: string; mine: string }> = {
  buyers: { plural: "קונים", mine: "שלי" },
  renters: { plural: "שוכרים", mine: "שלי" }
};

/**
 * The heading that marks where the top matches stop — the control that replaced the quality filter.
 *
 * It is a LABEL, not a control: nothing to press, nothing to discover, and no state that can
 * disagree with what is on screen. The list is one list in score order and this says where the line
 * falls. See the note on `partitionMatchRowsByQuality` for the four filters this replaced.
 *
 * The second line explains the cut in the realtor's terms rather than restating the threshold: what
 * she needs to know is that these rows have something missing, not what number they fall under.
 */
const LESS_PRECISE_HEADING_SX = {
  bgcolor: "background.neutral",
  borderBottom: "none",
  py: 1,
  // `paddingInline`, never `pr`/`pl`: stylis-plugin-rtl mirrors PHYSICAL properties and leaves the
  // logical ones alone, so the physical form flips under RTL and the padding lands on the wrong edge.
  paddingInline: 2
} as const;

const lessPreciseHeading = (count: number): JSX.Element => (
  <Stack spacing={0.25}>
    <Typography variant="subtitle2" color="text.secondary">
      {`פחות מדויק (${count})`}
    </Typography>
    <Typography variant="caption" color="text.disabled">
      לכל אחד מהם חסר משהו שביקש, או שההתאמה שלו נמוכה יותר
    </Typography>
  </Stack>
);

/**
 * The listing's matched buyers as a TABLE with multi-select and bulk actions — desktop table,
 * mobile cards, one selection model, one bulk bar. Modeled on /crm: the bar APPEARS with a
 * selection (it is absent, not disabled, otherwise), filters are exclusive toggle groups, and
 * changing a filter drops the selection so an action never reaches rows the realtor can no
 * longer see.
 */
export default function ListingMatchesTable({
  listing,
  listingId,
  audience,
  myContacts,
  orgGroups,
  orgAccess,
  refreshing,
  provisional,
  truncated,
  dense,
  onContactAddedToMinisite
}: Props): JSX.Element {
  const theme = useTheme();
  /**
   * ⚠️ THE LAYOUT SWITCH IS A CONTAINER QUESTION, NOT A VIEWPORT ONE.
   *
   * This table renders in three containers of very different widths: the listing dialog
   * (`maxWidth="md"` ⇒ ~790px of content on a 1440px screen), the batch dialog's card, and `dense`
   * inside a Collapse. `useMediaQuery(up("md"))` answered "is the WINDOW ≥ 900px", which is the
   * wrong question in all three — the same class of bug `ContactTableNestedRowTabs.tsx:39-52` added
   * its `compact` prop for. The media query survives only as the FIRST-PAINT SEED, before the
   * ResizeObserver has measured.
   */
  const isWideViewport = useMediaQuery(theme.breakpoints.up("md"));
  const { ref: surfaceRef, width: surfaceWidth } = useContainerWidth();
  /** Below this the 5-column table cannot hold a name, a score and a budget; cards instead. */
  const CARDS_MAX_WIDTH = 560;
  /** Below this the toggles, the row actions and the reason hints tighten but the table survives. */
  const COMPACT_MAX_WIDTH = 760;
  const useCards = surfaceWidth === null ? !isWideViewport : surfaceWidth < CARDS_MAX_WIDTH;
  const compact = Boolean(dense) || (surfaceWidth === null ? !isWideViewport : surfaceWidth < COMPACT_MAX_WIDTH);
  /** Every existing read of `isDesktop` keeps working; it now means "wide enough for the table". */
  const isDesktop = !useCards;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { user } = useAuth();
  const email = (user?.email as string | undefined) ?? "";
  const { userSettings } = useFirestore();
  const { saveListingPresentation, getListingPresentation } = useWebotServer();
  const { recordBulkMessage } = useContactWrites();
  const promptAndAddListingToBuyers = useAddListingWithEditsToBuyers();
  const hasOfficialWhatsapp = Boolean(userSettings?.advancedFeatures?.includes("officialWhatsapp"));
  // The same any-of gate as the audiences pages (routes: crm | officialWhatsapp | bulkMessaging) —
  // the listing dialog is reachable without any of them, and an audience its creator can never
  // open is worse than no button. Not officialWhatsapp alone: crm-only agencies use audiences with
  // the extension sender.
  const canUseAudiences = (["crm", "officialWhatsapp", "bulkMessaging"] as const).some((feature) =>
    userSettings?.advancedFeatures?.includes(feature)
  );

  const listedFor: ListingListedFor = (listing?.listingListedFor ?? listing?.transactionType) === "rent" ? "rent" : "sale";
  // Edits made in the contact side panel, keyed by the phone the row was BUILT with (its key) —
  // so the row reads right the moment the panel closes, before any refetch (see applyMatchRowPatch).
  const [rowPatches, setRowPatches] = useState<ReadonlyMap<string, MatchRowPatch>>(() => new Map());
  const allRows = useMemo(
    () =>
      buildMatchRows({ myContacts, orgGroups, viewerId: email, listedFor }).map((row) =>
        applyMatchRowPatch(row, row.contact.id ? rowPatches.get(row.contact.id) : undefined, listedFor)
      ),
    [myContacts, orgGroups, email, listedFor, rowPatches]
  );
  /**
   * ⚠️ EVERY MATCH IS ON SCREEN. The quality control is gone — see the long note on
   * `partitionMatchRowsByQuality` for the four versions of it that failed. The list is one list,
   * ordered by score, with a heading where the top matches end.
   */
  const rows = allRows;
  const mineCount = rows.filter((row) => row.access === "own").length;
  const orgCount = rows.length - mineCount;
  const { orgs: agencies } = useOrgDirectory();
  // A realtor who MAY see the office always gets the toggle — "של המשרד (0)" says the office was
  // searched and nothing matched. Hidden at zero, it read as "the office was never searched"
  // (reported 2026-09-18 on a listing where 48 of 51 office buyers wanted another neighbourhood).
  //
  // But "may see the office" is the SERVER's answer (`orgAccess`), not "is in an agency". That
  // guess offered an office tab — with an empty state claiming the office had been searched for
  // them — to a member whose agency had set their `pool` to `ללא`, i.e. to exactly the person the
  // agency had excluded. Rows already present win over a `hidden` verdict so a response that
  // crossed a permission change can never strand rows behind a toggle that is gone.
  const hasOrg = orgCount > 0 || (orgAccess === undefined ? (orgGroups?.length ?? 0) > 0 || agencies.length > 0 : orgAccess !== "hidden");

  const [scope, setScope] = useState<MatchScope>("mine");
  // The realtor picked the scope herself — it is then never changed for her (until the listing
  // changes, or the office toggle disappears).
  const scopeChosenByUserRef = useRef(false);
  // The current "org" was OUR pick — so it goes back to "mine" once own rows appear.
  const scopeAutoPickedRef = useRef(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<MatchRow | null>(null);
  const [bulkMessageOpen, setBulkMessageOpen] = useState(false);
  const [officialOpen, setOfficialOpen] = useState(false);
  const [whatsappChooserOpen, setWhatsappChooserOpen] = useState(false);
  /**
   * Which channel the realtor picked last. It PRE-SELECTS the card; it never skips the chooser —
   * skipping a dialog someone opted into once is how forty messages go down the wrong pipe.
   */
  const [lastWhatsappChannel, setLastWhatsappChannel] = useLocalStorage<WhatsappSendChannel | "">(
    email ? `webot:listingShareWhatsappChannel:${email}` : "webot:listingShareWhatsappChannel",
    ""
  );
  const handleChooseWhatsappChannel = (channel: WhatsappSendChannel): void => {
    setWhatsappChooserOpen(false);
    setLastWhatsappChannel(channel);
    // The two real dialogs stay where they are rather than nesting inside the chooser:
    // ListingShareOfficialDialog is fullScreen below sm and NewBulkMessageDialog is a multi-step
    // wizard, so nesting either would stack three backdrops over the listing modal.
    if (channel === "official") setOfficialOpen(true);
    else setBulkMessageOpen(true);
  };
  const [officialSent, setOfficialSent] = useState(false);
  // Which rows have their "פרטים נוספים" open. A SET, not one row: comparing two candidates side
  // by side is the whole point of reading them, and the rows stay put while open.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (key: string): void =>
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  // Per listing: a new listing starts on "mine" with nothing remembered. Declared BEFORE the auto
  // pick below so, when both run in one commit, the pick lands on top of the reset.
  useEffect(() => {
    scopeChosenByUserRef.current = false;
    scopeAutoPickedRef.current = false;
    setScope("mine");
  }, [listingId]);

  // Nothing of my own but colleagues have some: open where the rows are, not on an empty tab. The
  // pick is provisional — a snapshot (or a stale answer) can have no own rows that the live answer
  // then brings, and a sticky "org" hid them behind a toggle (reported 2026-09-18).
  useEffect(() => {
    if (myContacts === undefined || scopeChosenByUserRef.current) return;
    if (mineCount === 0 && orgCount > 0) {
      scopeAutoPickedRef.current = true;
      setScope("org");
    } else if (mineCount > 0 && scopeAutoPickedRef.current) {
      scopeAutoPickedRef.current = false;
      setScope("mine");
    }
  }, [myContacts, mineCount, orgCount]);

  // The office toggle is only rendered while there ARE office matches — a scope left on "org"/"all"
  // after it disappears would hide the realtor's own buyers with no control to get back.
  useEffect(() => {
    if (hasOrg) return;
    scopeAutoPickedRef.current = false;
    setScope("mine");
  }, [hasOrg]);

  const handleScopeChange = (_event: unknown, value: MatchScope | null): void => {
    if (!value) return;
    scopeChosenByUserRef.current = true;
    scopeAutoPickedRef.current = false;
    setScope(value);
  };

  /**
   * A scope or listing change drops the selection — the same rule as /crm (useCrmSelection) — and
   * closes every open detail panel, which would otherwise belong to rows no longer on screen.
   *
   * The quality filter used to be in this dependency list and is not a thing any more: every match
   * is always on screen, so nothing but these two can remove a selected row.
   */
  useEffect(() => {
    setSelected(new Set());
    setExpanded(new Set());
  }, [scope, listingId]);

  /**
   * What is on screen: everything in the active scope, in score order.
   *
   * `near` follows `top` rather than being interleaved, so the heading between them has exactly one
   * place to go. `rowsForScope` has already sorted by confidence and the partition is stable, so for
   * all but wide rows this is the order it already produced — a wide row scores inside the normal
   * range only by accident of rounding, and it belongs under the heading regardless of its number.
   */
  const { top: topRows, near: nearRows } = useMemo(
    () => partitionMatchRowsByQuality(rowsForScope(rows, scope)),
    [rows, scope]
  );
  const visible = useMemo(() => [...topRows, ...nearRows], [topRows, nearRows]);
  const qualityCounts = useMemo(
    () => ({ top: topRows.length, near: nearRows.length }),
    [topRows.length, nearRows.length]
  );
  /**
   * The row the "פחות מדויק" heading goes in front of.
   *
   * Shown whenever there IS a less-precise group — including when it is the whole list. A realtor
   * opening a listing with no top matches at all then reads "פחות מדויק" above the first row and
   * knows why everything looks weak, which is the fact the old "התאמות מובילות (0)" tab existed to
   * carry. No near rows, no heading: there is nothing to separate.
   */
  const firstNearKey = nearRows.length > 0 ? nearRows[0].key : null;
  const selectableVisible = useMemo(() => visible.filter(isRowSelectable), [visible]);

  // The "open on the rung that has rows" auto-relax that used to live here is gone with the rungs:
  // there is no empty default to rescue the realtor from when every match is already on screen.
  // Memoized: NewBulkMessageDialog re-runs a per-recipient Firestore lookup whenever `selected`
  // changes identity, so a fresh array on every render re-queried on every re-render.
  const selectedRows = useMemo(() => visible.filter((row) => selected.has(row.key)), [visible, selected]);
  // One recipient per phone — see uniqueRecipientRows. Every bulk action acts on THESE.
  const recipientRows = useMemo(() => uniqueRecipientRows(selectedRows), [selectedRows]);

  const minisite = useListingMinisiteState(listingId, rows.length > 0);

  // ── contact side panel ──────────────────────────────────────────────────────────────
  // A row click opens the CRM's own side panel, as on /crm — above this dialog (see
  // ContactPanelDrawer). The row is remembered as it was at the click and refreshed from `rows`
  // while it is still there: a background refresh that drops or re-sorts it must not pull the
  // panel out from under the realtor (the same rule /crm follows for its open row).
  // The agency the realtor last chose on /crm — the same stored value `CrmPage` reads (JSON-encoded,
  // hence the hook rather than a raw `localStorage.getItem`).
  const [lastCrmOrgId] = useLocalStorage<string>(CRM_LAST_ORG_STORAGE_KEY, "");
  const [panelRow, setPanelRow] = useState<MatchRow | null>(null);
  const openPanel = (row: MatchRow): void => {
    if (toCrmPanelRow(row)) setPanelRow(row);
  };
  useEffect(() => {
    setPanelRow(null);
    setRowPatches(new Map());
  }, [listingId]);
  const panelMatch = panelRow ? rows.find((row) => row.key === panelRow.key) ?? panelRow : null;
  const panelCrmRow = useMemo(() => (panelMatch ? toCrmPanelRow(panelMatch) : null), [panelMatch]);
  const panelIsOwn = panelMatch?.access === "own";
  // A pooled office's one record (`isOfficeRecordRow`): the viewer's own book whoever's list it
  // is on. The drawer decides what may be edited by the office's rule (held, or the full pool),
  // and the panel's edits land on the row whichever group it came in.
  const panelIsOffice = panelMatch ? isOfficeRecordRow(panelMatch) : false;
  // What the מתווך מטפל control sets: for a colleague's row the agency that GRANTS it; for an own row
  // the agency /crm would show (the realtor's last choice there, else its default).
  const panelOrgId = panelMatch
    ? resolveCrmOrgId(panelMatch.sharedOrgId ?? (lastCrmOrgId || null), agencies)
    : null;
  const panelSourceId = panelMatch?.contact.id;

  // A patch is an overlay until the NEXT ANSWER, never a cache — the same rule as /crm, whose
  // patched row "the next real fetch overwrites wholesale". Left permanent, it pinned a budget or
  // 🔥 edited elsewhere later (another tab, the full page) over every fresh answer, against the
  // row's own reason chips. The open panel's contact keeps its patch: its live document is the
  // authority while it is on screen.
  const panelSourceIdRef = useRef(panelSourceId);
  panelSourceIdRef.current = panelSourceId;
  const lastAnswerRef = useRef({ myContacts, orgGroups });
  useEffect(() => {
    if (lastAnswerRef.current.myContacts === myContacts && lastAnswerRef.current.orgGroups === orgGroups) return;
    lastAnswerRef.current = { myContacts, orgGroups };
    setRowPatches((current) => {
      if (current.size === 0) return current;
      const keep = panelSourceIdRef.current;
      const kept = keep ? current.get(keep) : undefined;
      return kept && keep ? new Map([[keep, kept]]) : new Map();
    });
  }, [myContacts, orgGroups]);
  const handlePanelPatched = useCallback(
    (patch: Partial<CrmContactRow>) => {
      // The panel only reports patches for the VIEWER's own row (never a colleague's opened own-first).
      if (!panelSourceId) return;
      // Only the keys the panel actually sent — an absent key must not blank the row's value.
      const picked = Object.fromEntries(
        PANEL_PATCH_KEYS.filter((key) => key in patch).map((key) => [key, patch[key]])
      ) as MatchRowPatch;
      setRowPatches((current) => {
        const next = new Map(current);
        next.set(panelSourceId, { ...current.get(panelSourceId), ...picked });
        return next;
      });
    },
    [panelSourceId]
  );
  const handlePanelRekeyed = useCallback(
    (_oldContactId: string, saved: Partial<CrmContactRow> & { contactId: string }) => {
      if (!panelSourceId) return;
      // The row (and the open panel, which reads it) move onto the NEW phone — every row action
      // would otherwise keep targeting the dead id until the next compute.
      setRowPatches((current) => {
        const next = new Map(current);
        next.set(panelSourceId, { ...current.get(panelSourceId), contactId: saved.contactId });
        return next;
      });
    },
    [panelSourceId]
  );
  /** Clicks inside a row's own controls (checkbox, actions, their portaled menus) never open the panel. */
  const stopRowClick = (event: { stopPropagation: () => void }): void => event.stopPropagation();

  // Selecting rows commits the realtor to the scope on screen: an automatic switch after that (a live
  // answer bringing own rows) would clear the selection — and empty a send dialog open on it.
  const claimScope = (): void => {
    scopeChosenByUserRef.current = true;
    scopeAutoPickedRef.current = false;
  };

  const toggleRow = (row: MatchRow): void => {
    if (!isRowSelectable(row)) return;
    claimScope();
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(row.key)) next.delete(row.key);
      else next.add(row.key);
      return next;
    });
  };
  const allSelected = selectableVisible.length > 0 && selectableVisible.every((row) => selected.has(row.key));
  const someSelected = selectedRows.length > 0 && !allSelected;
  const toggleAll = (): void => {
    if (!allSelected) claimScope();
    setSelected(allSelected ? new Set() : new Set(selectableVisible.map((row) => row.key)));
  };

  const selectedIds = useMemo(
    () => recipientRows.map((row) => row.contactId).filter((id): id is string => Boolean(id)),
    [recipientRows]
  );

  // ── row actions ─────────────────────────────────────────────────────────────────────
  const handleToggleMinisite = useCallback(
    async (row: MatchRow) => {
      if (!row.contactId) return;
      const favorite = minisite.favoriteByContact.get(row.contactId);
      setRowBusy(row.key);
      try {
        if (favorite?.id) {
          await removeListingFromFavorites(`favorite-listings-of-contact/${favorite.id}`, listingId);
        } else {
          await promptAndAddListingToBuyers({ listingId, contactPhones: [row.contactId], onContactAdded: onContactAddedToMinisite });
        }
      } catch (err) {
        console.error("ListingMatchesTable: minisite toggle failed", err);
        enqueueSnackbar("עדכון המיניסייט נכשל", { variant: "error" });
      } finally {
        setRowBusy(null);
      }
    },
    [minisite.favoriteByContact, listingId, promptAndAddListingToBuyers, onContactAddedToMinisite, enqueueSnackbar]
  );

  const handleShareWhatsApp = useCallback(
    async (row: MatchRow) => {
      if (!row.contactId || !email) return;
      // Open the tab synchronously (popup blockers only allow a window opened in the click), then
      // point it at the chat once the minisite link exists.
      const chatWindow = window.open("", "_blank");
      try {
        const favoriteId = await createFavoriteIfNotExistsAndAddListingToFavorites(email, row.contactId, listingId);
        onContactAddedToMinisite?.(row.contactId);
        const link = `${window.location.origin}${PATH_PUBLIC.myListings}/${favoriteId}/${listingId}`;
        const url = `https://api.whatsapp.com/send?phone=${row.contactId.replace(/\D/g, "")}&text=${encodeURIComponent(link)}`;
        if (chatWindow) chatWindow.location.href = url;
        else window.open(url, "_blank");
      } catch (err) {
        chatWindow?.close();
        console.error("ListingMatchesTable: share failed", err);
        enqueueSnackbar("יצירת הקישור נכשלה", { variant: "error" });
      }
    },
    [email, listingId, onContactAddedToMinisite, enqueueSnackbar]
  );

  /*
   * ── editing the listing's presentation ─────────────────────────────────────────────
   *
   * GLOBAL, not per buyer. This button sits on a per-LISTING screen, and until 2026-09-30 it wrote
   * into the one contact's minisite doc — so editing a listing for forty buyers meant opening it
   * forty times, and nothing propagated. It now reads and writes the realtor's single record for
   * the listing, and the server fans the values out to every minisite holding it.
   *
   * `editingRow` survives only as "which row's ⋮ menu opened this"; nothing about the save depends
   * on the contact any more.
   */
  const [presentation, setPresentation] = useState<REST_schema.GetListingPresentationResponse | null>(null);
  useEffect(() => {
    if (!editingRow) return;
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await getListingPresentation(listingId);
        if (!cancelled) setPresentation(loaded);
      } catch (err) {
        console.error("ListingMatchesTable: loading the listing presentation failed", err);
        if (!cancelled) setPresentation(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editingRow, listingId, getListingPresentation]);

  // Stable across re-renders: the edit dialog re-seeds its form whenever this changes identity, so
  // a fresh object per render wiped whatever the realtor was typing.
  const editingInitialEdits = useMemo<ListingPriorEdits | null>(
    () =>
      presentation
        ? {
            picturesVisibilityMode: presentation.picturesVisibilityMode,
            hiddenPictures: presentation.hiddenPictures,
            descriptionOverride: presentation.descriptionOverride ?? ""
          }
        : null,
    [presentation]
  );

  const handleSaveEdits = async (edits: ListingPriorEdits): Promise<void> => {
    try {
      const result = await saveListingPresentation({
        listingId,
        descriptionOverride: edits.descriptionOverride,
        picturesVisibilityMode: edits.picturesVisibilityMode,
        hiddenPictures: edits.hiddenPictures
      });
      // Say what actually happened. "נשמר" would hide the one fact the realtor needs to trust the
      // feature: that it reached everyone, not just the buyer whose row they clicked.
      enqueueSnackbar(
        result.appliedToMinisites > 0
          ? `ההגדרות נשמרו והוחלו על ${result.appliedToMinisites} מיניסייטים`
          : "ההגדרות נשמרו ויחולו על כל לקוח שיקבל את הנכס",
        { variant: "success" }
      );
    } catch (err) {
      console.error("ListingMatchesTable: saving the listing presentation failed", err);
      enqueueSnackbar("שמירת העריכה נכשלה", { variant: "error" });
    }
  };

  // ── bulk actions ────────────────────────────────────────────────────────────────────
  const handleBulkAddToMinisite = async (): Promise<void> => {
    // Counted per contact: the hook swallows its own errors, so "it returned" says nothing about
    // how many writes landed.
    let added = 0;
    await promptAndAddListingToBuyers({
      listingId,
      contactPhones: selectedIds,
      onContactAdded: (phone) => {
        added += 1;
        onContactAddedToMinisite?.(phone);
      },
      setLoading: setBulkBusy
    });
    if (added === selectedIds.length) {
      enqueueSnackbar(`הנכס נוסף למיניסייט של ${added} לקוחות`, { variant: "success" });
      setSelected(new Set());
    } else if (added > 0) {
      enqueueSnackbar(`הנכס נוסף למיניסייט של ${added} מתוך ${selectedIds.length} לקוחות. נסו שוב עבור השאר.`, { variant: "warning" });
    } else {
      enqueueSnackbar("הוספת הנכס למיניסייט נכשלה", { variant: "error" });
    }
  };

  // The extension dialog is handed its audience directly (this surface never loads the whole
  // book), so it MUST also be handed a server-backed recorder — its default `updateContact`
  // reads `allContacts` and would mint a phantom contact. Same contract as /crm's send menu.
  const bulkMessageContacts = useMemo(
    () => recipientRows.map((row) => ({ ...row.contact, id: row.contactId }) as unknown as RealEstateContact),
    [recipientRows]
  );
  const ownerByContact = useMemo(() => new Map(recipientRows.map((row) => [row.contactId, row] as const)), [recipientRows]);
  const recordSend = useCallback(
    async (contactId: string, outcome: "sent" | "number_missing", sentMessage?: BulkMessageSentMessage) => {
      const row = ownerByContact.get(contactId);
      if (!row) return;
      try {
        await recordBulkMessage({
          // A colleague's contact is recorded through the agency scope, NAMING the shared org — a
          // member of several agencies is otherwise refused (CrmScopeAmbiguous) and the outcome lost.
          // A pooled office's record a colleague holds goes the same way, under the office's key
          // (`row.ownerId`): the record the viewer does not hold is the office's, not the colleague's.
          ...(row.access === "own"
            ? {}
            : { view: "org" as const, ownerUserId: row.ownerId, ...(row.sharedOrgId ? { orgId: row.sharedOrgId } : {}) }),
          contactId,
          outcome,
          ...(outcome === "sent" ? { sentMessage: { message: sentMessage?.message, mediaLink: sentMessage?.mediaLink } } : {})
        });
      } catch (err) {
        // Best effort: the message is already sent by the time this runs.
        console.error("Recording the bulk-send outcome failed (the message itself was sent)", err);
      }
    },
    [ownerByContact, recordBulkMessage]
  );

  const officialRecipients = useMemo(
    () => recipientRows.map((row) => ({ contactId: row.contactId as string, displayName: row.displayName })),
    [recipientRows]
  );

  // ── rendering ───────────────────────────────────────────────────────────────────────
  const noun = AUDIENCE_NOUN[audience];
  // Nothing known yet — or an EMPTY answer while a newer one is being computed: an empty list is
  // not final while the compute that may fill it runs, so it never renders "no buyers" meanwhile.
  const isLoading = myContacts === undefined || (Boolean(refreshing) && rows.length === 0);

  const toolbar = (
    <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
      {hasOrg && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={scope}
          onChange={handleScopeChange}
        >
          <ToggleButton value="mine" sx={{ px: 1.5, whiteSpace: "nowrap", minHeight: compact ? 40 : 34 }}>
            {`${noun.plural} ${noun.mine} (${mineCount})`}
          </ToggleButton>
          <ToggleButton value="org" sx={{ px: 1.5, whiteSpace: "nowrap", minHeight: compact ? 40 : 34 }}>
            {`של המשרד (${orgCount})`}
          </ToggleButton>
          <ToggleButton value="all" sx={{ px: 1.5, whiteSpace: "nowrap", minHeight: compact ? 40 : 34 }}>
            הכל
          </ToggleButton>
        </ToggleButtonGroup>
      )}
      <Box sx={{ flexGrow: 1 }} />
      {/*
        Only while nothing is selected — after that the bulk bar carries "ביטול בחירה".

        ⚠️ THE COUNT ON THE LABEL IS LOAD-BEARING NOW THAT THERE IS NO FILTER. It used to be
        "everything the top rung is showing", and the rung was the safety: a realtor who pressed it
        got the good matches because nothing else was on screen. The list now holds every match, so
        this selects the less-precise ones too — and the only thing saying so is this number. Keep
        it, and keep the "פחות מדויק" heading visible in the list it selects from.
      */}
      {!isDesktop && selectableVisible.length > 0 && selectedRows.length === 0 && (
        <Button size="small" color="inherit" onClick={toggleAll}>
          {`בחירת הכל (${selectableVisible.length})`}
        </Button>
      )}
      {refreshing && !isLoading && (
        <Typography variant="caption" color="text.secondary">
          מעדכן…
        </Typography>
      )}
      {truncated && !isLoading && (
        <Typography variant="caption" color="text.secondary" sx={{ flexBasis: "100%" }}>
          {refreshing ? "מוצגות ההתאמות הראשונות — הרשימה המלאה מתעדכנת" : "מוצגות ההתאמות הראשונות בלבד — רעננו לרשימה המלאה"}
        </Typography>
      )}
    </Stack>
  );

  // ── הוספה לקהל ── /crm's own dialog, fed the selected rows as explicit members (own AND
  // colleagues' — `from-selection` can express only one CRM scope per request). FROZEN when the
  // dialog opens: a live answer landing meanwhile must not change the set the count promised.
  const [audienceDraft, setAudienceDraft] = useState<
    (ReturnType<typeof toAudienceMembers> & { name: string; description: string }) | null
  >(null);
  const [audienceOpen, setAudienceOpen] = useState(false);
  const listingAddressText = (() => {
    if (!listing?.address) return "";
    const parsed = parseListingAddressByDisplayMode(listing.address, listing.addressDisplayMode ?? "full");
    const streetLine = parsed.street ? [parsed.street, parsed.houseNumber].filter(Boolean).join(" ") : "";
    return getFullAddress({ address: streetLine, neighborhood: parsed.neighborhood, city: parsed.city });
  })();
  function openAudienceDialog(): void {
    const draft = toAudienceMembers(recipientRows);
    if (draft.members.length === 0) {
      enqueueSnackbar("אין בבחירה אנשי קשר שאפשר להוסיף לקהל", { variant: "info" });
      return;
    }
    setAudienceDraft({
      ...draft,
      name: defaultMatchAudienceName(listingAddressText, audience, new Date()),
      description: listingAddressText ? `התאמות לנכס ${listingAddressText}` : `התאמות לנכס ${listingId}`
    });
    setAudienceOpen(true);
  }
  const handleAudienceCompleted = (summary: string, result?: { groupId: string }): void => {
    const groupId = result?.groupId;
    enqueueSnackbar(summary, {
      variant: "success",
      // A new tab, never navigation: the listing dialog is global and would stay open over the
      // audience page (ListingDialogContext does not close on route change). 10s, not the app's 1s
      // default — an action toast gone before the pointer reaches it is no action at all.
      ...(groupId
        ? {
            autoHideDuration: 10_000,
            action: (key: string | number) => (
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  closeSnackbar(key);
                  window.open(PATH_DASHBOARD.general.whatsappAudienceDetail(groupId), "_blank", "noopener");
                }}
              >
                פתיחת הקהל
              </Button>
            )
          }
        : {})
    });
    setSelected(new Set());
  };

  // Snapshot rows are a projection of the contact, and the extension dialog reads `row.contact` for
  // its skip flags and template variables — so that send waits for the live answer (see the
  // `provisional` prop). The official send and the minisite need only the phone (the server loads
  // the contact itself), so they stay on. The reason says what is actually happening: when the live
  // compute failed nothing is updating, and the realtor has to refresh.
  const extensionDisabledReason = provisional
    ? refreshing
      ? "רגע, מעדכנים את הנתונים…"
      : "ההתאמות מוצגות מהחישוב הקודם — רעננו את ההתאמות לפני דיוור בתוסף"
    : undefined;
  // An audience outlives this screen, and once a COLLEAGUE's buyer is in it no send gate reads that
  // colleague's opt-out again — the skip in toAudienceMembers is the only one. It reads the row's
  // flag, and a snapshot's flag can be weeks old, so a selection with colleagues' rows waits for the
  // live answer, like the extension send. Own rows stay protected at send time by the viewer's own
  // opt-out, so an own-only selection is fine on a snapshot.
  const audienceDisabledReason =
    provisional && recipientRows.some((row) => row.access !== "own")
      ? refreshing
        ? "רגע, מעדכנים את הנתונים…"
        : "ההתאמות מוצגות מהחישוב הקודם — רעננו את ההתאמות לפני הוספה לקהל"
      : undefined;
  const bulkActions: BulkAction[] = [
    {
      key: "minisite",
      label: "הוספה למיניסייט",
      icon: <FavoriteRoundedIcon fontSize="small" />,
      onClick: () => void handleBulkAddToMinisite(),
      loading: bulkBusy
    },
    ...(canUseAudiences
      ? [
          {
            key: "audience",
            label: "הוספה לקהל",
            icon: <GroupAddRoundedIcon fontSize="small" />,
            onClick: openAudienceDialog,
            disabledReason: audienceDisabledReason
          }
        ]
      : []),
    /*
     * ONE WhatsApp button (2026-09-30). It was two — "דיוור בתוסף" and "וואטסאפ רשמי" — which asked
     * the realtor to know the difference between two delivery mechanisms before she had decided to
     * send anything. Now the question is "send on WhatsApp?" and the mechanism is chosen inside.
     *
     * ⚠️ `extensionDisabledReason` deliberately does NOT disable this button. It is the EXTENSION's
     * limitation (that sender reads `row.contact`; the official send only needs the phone, because
     * the server loads the contact itself), so it travels into the chooser as that one option's
     * reason. Previously it disabled the extension button only, and the official button was fine —
     * gating the shared button on it would newly block a send that works.
     */
    {
      key: "whatsapp",
      label: "שליחה בוואטסאפ",
      shortLabel: "וואטסאפ",
      icon: <WhatsAppIcon fontSize="small" />,
      onClick: () => setWhatsappChooserOpen(true)
    }
  ];
  // A disabled button fires no events, so its Tooltip needs a wrapper that does.
  const withDisabledReason = (action: BulkAction, button: JSX.Element, fill: boolean): JSX.Element =>
    action.disabledReason ? (
      <Tooltip key={action.key} title={action.disabledReason}>
        <Box component="span" sx={fill ? { display: "flex", minWidth: 0 } : { display: "inline-flex", flexShrink: 0 }}>
          {button}
        </Box>
      </Tooltip>
    ) : (
      button
    );
  const bulkCount = (
    <Typography variant="subtitle2" sx={{ flexShrink: 0, color: "primary.darker" }}>
      <Box component="span" sx={{ fontWeight: 700 }}>
        {recipientRows.length}
      </Box>{" "}
      נבחרו
    </Typography>
  );
  const clearSelectionButton = (
    <Button size="small" color="inherit" onClick={() => setSelected(new Set())} sx={{ flexShrink: 0 }}>
      ביטול בחירה
    </Button>
  );

  const bulkBar =
    selectedRows.length > 0 ? (
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 3,
          bgcolor: "primary.lighter",
          borderRadius: 1,
          boxShadow: 3
        }}
      >
        {isDesktop ? (
          // Wraps rather than clips: four actions do not fit the dense batch dialog's width.
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1, flexWrap: "wrap", rowGap: 0.5 }}>
            {bulkCount}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            {bulkActions.map((action) =>
              withDisabledReason(
                action,
                <LoadingButton
                  key={action.key}
                  size="small"
                  color="inherit"
                  loading={action.loading}
                  disabled={Boolean(action.disabledReason)}
                  startIcon={action.icon}
                  onClick={action.onClick}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  {action.label}
                </LoadingButton>,
                false
              )
            )}
            <Box sx={{ flexGrow: 1 }} />
            {clearSelectionButton}
          </Stack>
        ) : (
          // Mobile: a horizontally scrolling row hid the last actions off-screen. Two rows
          // instead — the count and "ביטול" on top, then every action as an equal tile.
          <Stack sx={{ px: 1.5, py: 1 }} spacing={0.75}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              {bulkCount}
              {clearSelectionButton}
            </Stack>
            <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${bulkActions.length}, 1fr)`, gap: 0.5 }}>
              {bulkActions.map((action) =>
                withDisabledReason(
                  action,
                  <LoadingButton
                    key={action.key}
                    color="inherit"
                    loading={action.loading}
                    disabled={Boolean(action.disabledReason)}
                    onClick={action.onClick}
                    sx={{
                      flexDirection: "column",
                      gap: 0.25,
                      py: 0.75,
                      minWidth: 0,
                      width: "100%",
                      typography: "caption",
                      fontWeight: 600,
                      lineHeight: 1.2
                    }}
                  >
                    {action.icon}
                    {action.shortLabel ?? action.label}
                  </LoadingButton>,
                  true
                )
              )}
            </Box>
          </Stack>
        )}
      </Box>
    ) : null;

  const rowCheckbox = (row: MatchRow): JSX.Element => {
    const checkbox = (
      <Checkbox
        size="small"
        checked={selected.has(row.key)}
        disabled={!isRowSelectable(row)}
        onChange={() => toggleRow(row)}
        inputProps={{ "aria-label": `בחירת ${row.displayName}` }}
      />
    );
    // ⚠️ BOTH branches render the SAME box, and the wrapper must be INVISIBLE to layout.
    //
    // A disabled Checkbox fires no events, so a colleague's row needs a wrapper for its tooltip.
    // Two things went wrong here in a row. First the wrapper was a bare `<span>` on that branch
    // only, so office rows sat a few pixels off the realtor's own. Then the wrapper went on both
    // branches — which moved the WHOLE column: MUI's `size="small"` checkbox cell is a fixed 48px
    // made of a 20px control plus 28px of cell padding, and it strips the control's own 9px
    // padding with `.MuiTableCell-paddingCheckbox > * { padding: 0 }`. That selector matches the
    // DIRECT child, so once a wrapper sat in between, the checkbox kept its padding, the cell
    // overflowed its fixed 48px and pushed into the name column — the checkbox ended up jammed
    // against the avatar, misaligned against the header's own (unwrapped) checkbox, and the name
    // column lost the width to blank space (reported 2026-09-24 with a screenshot).
    //
    // `CHECKBOX_CELL_SX` on the desktop cell re-applies that `padding: 0` through the wrapper. It
    // lives on the CELL, not here: the mobile card wants the control's normal touch target.
    const cell = (
      <Box component="span" sx={{ display: "inline-flex" }}>
        {checkbox}
      </Box>
    );
    return isRowSelectable(row) ? cell : <Tooltip title="אין לך הרשאה לפרטי הלקוח הזה — רק לסוכן המטפל">{cell}</Tooltip>;
  };

  const renderDesktop = (): JSX.Element => (
    <Card variant="outlined" sx={{ overflow: "visible" }}>
      <Table size="small" sx={{ tableLayout: "fixed", "& td, & th": { borderColor: "divider" } }}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" sx={{ width: 48, ...CHECKBOX_CELL_SX }}>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                disabled={selectableVisible.length === 0}
                onChange={toggleAll}
                inputProps={{ "aria-label": "בחירת כל השורות" }}
              />
            </TableCell>
            {/* 28% → 32%: the identity cell gained the CRM status chip and the created/updated
                line. The budget (max "2.15 מיליון ₪" ≈ 88px) and the four 34px action buttons both
                had slack; the match column is `auto` and absorbs the rest. */}
            <TableCell sx={{ width: "32%" }}>לקוח</TableCell>
            <TableCell>התאמה</TableCell>
            <TableCell sx={{ width: 112 }}>תקציב</TableCell>
            <TableCell sx={{ width: 156 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((row) => {
            const inMinisite = Boolean(row.contactId && minisite.favoriteByContact.has(row.contactId));
            const openable = Boolean(toCrmPanelRow(row));
            const isExpanded = expanded.has(row.key);
            return (
              // A Fragment, not `<>`: the row and its details panel are two <tr>s of one list item,
              // and only the long form can carry the key.
              <Fragment key={row.key}>
              {row.key === firstNearKey && (
                <TableRow sx={{ "&:hover": { bgcolor: "transparent" } }}>
                  {/* Spans the whole table so the heading reads as a divider, not as a row with
                      empty cells. Not selectable and not clickable: it is a label. */}
                  <TableCell colSpan={5} sx={LESS_PRECISE_HEADING_SX}>
                    {lessPreciseHeading(qualityCounts.near)}
                  </TableCell>
                </TableRow>
              )}
              <TableRow
                hover
                selected={selected.has(row.key)}
                /*
                 * THE ROW EXPANDS; THE NAME OPENS THE PANEL (2026-09-30, inverted from the reverse).
                 *
                 * `ContactTableRow.tsx:191-201` documents the OPPOSITE decision for /contacts, and
                 * both are right for their surface: there the row is the only route to the customer,
                 * whereas here the thing a realtor opens a match for is WHY it matched — and that is
                 * the panel underneath. The contact card is still one click away, on the name.
                 *
                 * A redacted colleague row has no identity to open but does have reasons, so it
                 * expands like any other; only its name is inert.
                 */
                onClick={() => toggleExpanded(row.key)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && event.target === event.currentTarget) toggleExpanded(row.key);
                }}
                tabIndex={0}
                aria-expanded={isExpanded}
                aria-label={`פרטי ההתאמה של ${row.displayName}`}
                sx={{
                  "& td": { py: dense ? 0.75 : 1.25, verticalAlign: "middle" },
                  cursor: "pointer",
                  ...(panelMatch?.key === row.key ? { bgcolor: "action.selected" } : {})
                }}
              >
                <TableCell padding="checkbox" onClick={stopRowClick} sx={CHECKBOX_CELL_SX}>
                  {rowCheckbox(row)}
                </TableCell>
                <TableCell>
                  <MatchIdentity
                    row={row}
                    dense={dense}
                    compact={compact}
                    onOpenPanel={openable ? openPanel : undefined}
                  />
                </TableCell>
                <TableCell onClick={stopRowClick}>
                  {/* The score ring leads the cell; beside it only the warning (when the card is
                      too thin to judge) and ONE text button. The nine reason chips that used to
                      live here are in the panel the button opens — see MatchReasonDetails. The
                      cell stops the row click so opening the details never also opens the CRM
                      side panel. */}
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                    <MatchScore row={row} size={dense ? 38 : 42} />
                    <Stack spacing={0.25} alignItems="flex-start" sx={{ minWidth: 0, overflow: "hidden", flexGrow: 1 }}>
                      <MatchLooseBadge row={row} />
                      <MatchMissingBadge row={row} />
                      <MatchDataWarning row={row} />
                      <MatchDetailsToggle open={isExpanded} gaps={countMatchGaps(row.reasons)} onClick={() => toggleExpanded(row.key)} />
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell>
                  <MatchBudgetCell row={row} />
                </TableCell>
                <TableCell onClick={stopRowClick}>
                  {/* The minisite state rides the heart (filled = in the minisite) plus the view
                      count beside it — a column of its own cost the reasons their width. The cell
                      stops the row CLICK: React bubbles through portals, so a pick in the ⋮ menu
                      would otherwise also open the panel. Keys are NOT stopped — Escape must still
                      reach the listing dialog; the row's Enter only fires on the row itself. */}
                  <MatchRowActions
                    row={row}
                    inMinisite={inMinisite}
                    views={row.contactId ? minisite.viewsByContact.get(row.contactId) : undefined}
                    busy={rowBusy === row.key}
                    onToggleMinisite={(r) => void handleToggleMinisite(r)}
                    onShareWhatsApp={(r) => void handleShareWhatsApp(r)}
                    onEditInMinisite={setEditingRow}
                  />
                </TableCell>
              </TableRow>
              {/* The details open UNDER the row, full width — a popover would cover the rows the
                  realtor is comparing it against, and the panel is a short read, not a dialog. */}
              <TableRow>
                <TableCell colSpan={5} sx={{ py: 0, borderBottom: isExpanded ? undefined : "none" }}>
                  <Collapse in={isExpanded} unmountOnExit>
                    <Box sx={{ px: 1 }}>
                      <MatchReasonDetails
                        reasons={row.reasons}
                        contactName={row.access === "redacted" ? undefined : row.displayName}
                        compact={compact}
                        createdTime={row.createdTime}
                        updatedTime={row.updatedTime}
                      />
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );

  const renderMobile = (): JSX.Element => (
    <Stack spacing={1.25}>
      {visible.map((row) => {
        const inMinisite = Boolean(row.contactId && minisite.favoriteByContact.has(row.contactId));
        const views = row.contactId ? minisite.viewsByContact.get(row.contactId) : undefined;
        const openable = Boolean(toCrmPanelRow(row));
        const isExpanded = expanded.has(row.key);
        return (
          <Fragment key={row.key}>
          {row.key === firstNearKey && (
            // Rounded, or the neutral band reads as a card that failed to render.
            <Box sx={{ ...LESS_PRECISE_HEADING_SX, borderRadius: 1, mt: 0.5 }}>
              {lessPreciseHeading(qualityCounts.near)}
            </Box>
          )}
          <Card
            variant="outlined"
            // A tap EXPANDS the card; the name opens the contact's side panel (the CRM's bottom
            // sheet on a phone). Same inversion as the desktop row — see the comment there.
            onClick={() => toggleExpanded(row.key)}
            sx={{
              p: 1.5,
              /*
               * MUI's `Card` root is `overflow: hidden` (theme/overrides/Card.ts keeps it, with a
               * `zIndex: 0` note about Safari + border-radius). The desktop Card opts out; this one
               * did not, so it SHAVED the expanded reasons panel — the "on mobile פרטים נוספים is
               * cut" report. The root cause is the mirrored list padding in MatchReasonDetails, but
               * this is what turned an overhang into an invisible truncation.
               */
              overflow: "visible",
              cursor: "pointer",
              borderColor: selected.has(row.key) ? "primary.main" : "divider",
              bgcolor: selected.has(row.key) ? "primary.lighter" : "background.paper"
            }}
          >
            <Stack spacing={1.25}>
              <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                <Box sx={{ mt: -0.5, ml: -0.5 }} onClick={stopRowClick}>
                  {rowCheckbox(row)}
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <MatchIdentity row={row} compact onOpenPanel={openable ? openPanel : undefined} />
                </Box>
                <MatchScore row={row} size={40} />
              </Stack>
              <Stack spacing={0.5} alignItems="flex-start" onClick={stopRowClick} sx={{ minWidth: 0, width: "100%" }}>
                <MatchLooseBadge row={row} />
                <MatchMissingBadge row={row} />
                <MatchDataWarning row={row} />
                <MatchDetailsToggle open={isExpanded} gaps={countMatchGaps(row.reasons)} onClick={() => toggleExpanded(row.key)} />
                <Collapse in={isExpanded} unmountOnExit sx={{ width: "100%" }}>
                  <MatchReasonDetails
                    reasons={row.reasons}
                    contactName={row.access === "redacted" ? undefined : row.displayName}
                    compact={compact}
                    createdTime={row.createdTime}
                    updatedTime={row.updatedTime}
                  />
                </Collapse>
              </Stack>
              {/* `flexWrap` + `minWidth: 0`: at ~256px (a phone, inside the listing dialog) the
                  budget text and four icon buttons cannot share a line. */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                justifyContent="space-between"
                flexWrap="wrap"
                rowGap={0.5}
                sx={{ minWidth: 0 }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                  <MatchBudgetCell row={row} />
                  {inMinisite && <MatchMinisiteCell inMinisite views={views} />}
                </Stack>
                <Box onClick={stopRowClick} sx={{ flexShrink: 0 }}>
                  <MatchRowActions
                    row={row}
                    inMinisite={inMinisite}
                    busy={rowBusy === row.key}
                    onToggleMinisite={(r) => void handleToggleMinisite(r)}
                    onShareWhatsApp={(r) => void handleShareWhatsApp(r)}
                    onEditInMinisite={setEditingRow}
                  />
                </Box>
              </Stack>
            </Stack>
          </Card>
          </Fragment>
        );
      })}
    </Stack>
  );

  const renderSkeleton = (): JSX.Element => (
    <Stack spacing={1}>
      {/* Said out loud: a bare skeleton for ~25s read as "the results are not available". */}
      <Typography variant="body2" color="text.secondary">
        מחשבים התאמות לנכס…
      </Typography>
      {[0, 1, 2].map((index) => (
        <Skeleton key={index} variant="rectangular" height={dense ? 44 : 56} sx={{ borderRadius: 1 }} />
      ))}
    </Stack>
  );

  const orgStillLoading = scope !== "mine" && orgGroups === undefined;

  return (
    // `surfaceRef` measures THIS element: every layout switch below is a question about how wide
    // the table actually is, not about the window. See `useContainerWidth`.
    <Stack spacing={2} ref={surfaceRef}>
      {toolbar}
      {bulkBar}
      {isLoading || (orgStillLoading && visible.length === 0) ? (
        renderSkeleton()
      ) : visible.length === 0 ? (
        // No rung to offer any more, so the empty state only ever answers the real question: was
        // anything searched, and where. An empty list now genuinely means "nobody matched".
        scope === "org" ? (
          // The office WAS searched — say so, rather than the generic "no buyers" illustration.
          <Stack spacing={0.5} alignItems="center" sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="subtitle2">{`אין ${noun.plural} של המשרד שמתאימים לנכס הזה`}</Typography>
            <Typography variant="caption" color="text.secondary">
              חיפשנו בלקוחות של חברי המשרד שיש לך גישה אליהם — אף אחד מהם לא עונה על הדרישות של הנכס.
            </Typography>
          </Stack>
        ) : (
          <NoBuyersFoundIllustration audience={audience} />
        )
      ) : isDesktop ? (
        renderDesktop()
      ) : (
        renderMobile()
      )}

      <ContactPanelDrawer
        contactId={panelCrmRow?.contactId ?? null}
        row={panelCrmRow}
        // /crm's two tabs: an own row is the "לקוחות שלי" panel; a colleague's row the "של
        // המשרד" one (own-first: the viewer's copy of the person, else only the timeline).
        scopeIsOwnBook={panelIsOwn}
        orgId={panelOrgId}
        canEdit={panelIsOffice ? undefined : panelIsOwn}
        onClose={() => setPanelRow(null)}
        onContactPatched={panelIsOwn || panelIsOffice ? handlePanelPatched : undefined}
        onContactRekeyed={panelIsOwn || panelIsOffice ? handlePanelRekeyed : undefined}
      />

      <EditListingInMinisiteDialog
        open={Boolean(editingRow)}
        listingId={listingId}
        pictures={
          listing?.imageUrls?.length
            ? listing.imageUrls.map((url, index) => ({ id: index.toString(), src: url, alt: url }))
            : null
        }
        description={listing?.description ?? ""}
        initialEdits={editingInitialEdits}
        minisiteCount={presentation?.minisiteCount ?? 0}
        customPresentationCount={presentation?.customPresentationCount ?? 0}
        onConfirm={(edits) => void handleSaveEdits(edits)}
        onClose={() => {
          setEditingRow(null);
          setPresentation(null);
        }}
      />

      {bulkMessageOpen && (
        <NewBulkMessageDialog
          open={bulkMessageOpen}
          listing={listing ? ({ ...listing, objectID: listing.objectID ?? listingId } as unknown as AlgoliaHit<ProcessedListingData>) : undefined}
          selected={selectedIds}
          contacts={bulkMessageContacts}
          recordSend={recordSend}
          onClose={() => setBulkMessageOpen(false)}
        />
      )}

      {/* Outside the bulk bar: the bar unmounts when the selection empties, and completion clears
          the selection before the dialog closes. */}
      {canUseAudiences && audienceDraft && (
        <CrmBulkAddToAudienceDialog
          open={audienceOpen}
          count={audienceDraft.members.length}
          selectedRows={[]}
          explicitMembers={{ members: audienceDraft.members, description: audienceDraft.description }}
          defaultName={audienceDraft.name}
          notice={
            audienceDraft.skippedColleagueOptOuts > 0
              ? `${audienceDraft.skippedColleagueOptOuts} מהנבחרים הם לקוחות של סוכנים אחרים שביקשו לא לקבל הודעות תפוצה — הם לא יתווספו לקהל.`
              : undefined
          }
          onClose={() => setAudienceOpen(false)}
          onCompleted={handleAudienceCompleted}
        />
      )}

      {/* Mounted only while open, so the extension-liveness poll inside it starts when the realtor
          asks how to send — not merely because the listing dialog is on screen. */}
      {whatsappChooserOpen && (
        <ListingShareWhatsappChooserDialog
          open
          onClose={() => setWhatsappChooserOpen(false)}
          onChoose={handleChooseWhatsappChannel}
          recipientCount={recipientRows.length}
          hasOfficialFeature={hasOfficialWhatsapp}
          extensionDisabledReason={extensionDisabledReason}
          lastChannel={lastWhatsappChannel}
        />
      )}

      {/* Gated on `officialOpen`, not on the feature flag: the flag was never the capability —
          `useWhatsappNumbers` is — and `officialOpen` can now only be set from an ENABLED chooser
          card, which already requires a connected number. The flag survives inside the chooser
          purely to pick which disabled sentence to show. */}
      {officialOpen && (
        <ListingShareOfficialDialog
          open
          onClose={() => {
            setOfficialOpen(false);
            // Cleared on CLOSE, not on send: the receipt the dialog shows names these recipients.
            if (officialSent) setSelected(new Set());
            setOfficialSent(false);
          }}
          listingId={listingId}
          recipients={officialRecipients}
          onSent={() => setOfficialSent(true)}
        />
      )}
    </Stack>
  );
}
